import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Divider,
} from '@mui/material';
import { beautifyNumber } from '../../../Utils/FormatNumber';

const sectionTitleSx = {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#304FFF',
    mb: 1,
};

const headerCellSx = {
    backgroundColor: '#BFC8FF',
    color: '#304FFF',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    textAlign: 'center',
    py: 1,
};

const infoRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    py: 0.5,
    fontSize: '0.9rem',
};

const collectUniqueAccountValues = (rows, field) => [
    ...new Set(
        (rows || [])
            .map((row) => row?.[field])
            .filter((value) => value != null && value !== '')
    ),
];

const formatAccountValues = (values, formatAccountLabel) => {
    if (!values.length) return '-';
    return values
        .map((value) => (formatAccountLabel ? formatAccountLabel(value) : value))
        .join(', ');
};

const TransactionRowsTable = ({ rows, formatDate, t, typeLabel, amountColumnLabel }) => {
    if (!rows?.length) {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                {t('NoData')}
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerCellSx}>{t('TransactionDate')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('Description')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('Reference')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('Type')}</TableCell>
                        <TableCell sx={headerCellSx}>{amountColumnLabel}</TableCell>
                        <TableCell sx={headerCellSx}>{t('UploadNumber')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={`${typeLabel}-${row.id}`} hover>
                            <TableCell align="center">
                                {row.documentDate ? formatDate(row.documentDate) : '-'}
                            </TableCell>
                            <TableCell align="center">{row.description || '-'}</TableCell>
                            <TableCell align="center">{row.reference || '-'}</TableCell>
                            <TableCell align="center">{row.type ? t(row.type) : '-'}</TableCell>
                            <TableCell align="center">{beautifyNumber(row.amount)}</TableCell>
                            <TableCell align="center">{row.uploadNumber ?? '-'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ViewTransactionMatchModal = ({
    open,
    onClose,
    match,
    loading,
    formatDate,
    t,
    direction = 'rtl',
    formatAccountLabel,
    isForeignCurrency = false,
    onRemoveMatch,
    isRemovingMatch = false,
}) => {
    const internalRows = match?.internalTransactions || [];
    const amountColumnLabel = isForeignCurrency ? t('CurrencyAmount') : t('Amount');
    const debitAccounts = formatAccountValues(
        collectUniqueAccountValues(internalRows, 'debitAccount'),
        formatAccountLabel
    );
    const creditAccounts = formatAccountValues(
        collectUniqueAccountValues(internalRows, 'creditAccount'),
        formatAccountLabel
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            dir={direction}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '12px',
                    p: 0.5,
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700, color: '#304FFF', textAlign: 'center', pb: 1 }}>
                {t('ViewTransactionMatch')}
            </DialogTitle>

            <DialogContent dividers sx={{ minHeight: 200 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : !match ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        {t('MatchDetailsNotFound')}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#F8FAFF' }}>
                            <Box sx={infoRowSx}>
                                <Typography component="span" fontWeight={600}>{t('MatchNumber')}:</Typography>
                                <Typography component="span">{match.matchNumber}</Typography>
                            </Box>
                            <Box sx={infoRowSx}>
                                <Typography component="span" fontWeight={600}>{t('TransactionDate')}:</Typography>
                                <Typography component="span">
                                    {match.transactionDate ? formatDate(match.transactionDate) : '-'}
                                </Typography>
                            </Box>
                            <Box sx={infoRowSx}>
                                <Typography component="span" fontWeight={600}>{t('MatchDate')}:</Typography>
                                <Typography component="span">
                                    {match.matchDate ? formatDate(match.matchDate) : '-'}
                                </Typography>
                            </Box>
                            <Box sx={infoRowSx}>
                                <Typography component="span" fontWeight={600}>{t('AccountCardNumber')}:</Typography>
                                <Typography component="span">{match.accountCodeId || '-'}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={infoRowSx}>
                                <Typography component="span" fontWeight={600}>{t('Debit Account')}:</Typography>
                                <Typography component="span">{debitAccounts}</Typography>
                            </Box>
                            <Box sx={infoRowSx}>
                                <Typography component="span" fontWeight={600}>{t('Credit Account')}:</Typography>
                                <Typography component="span">{creditAccounts}</Typography>
                            </Box>
                        </Paper>

                        <Box>
                            <Typography sx={sectionTitleSx}>{t('BankCommands')}</Typography>
                            <TransactionRowsTable
                                rows={match.externalTransactions}
                                formatDate={formatDate}
                                t={t}
                                typeLabel="external"
                                amountColumnLabel={amountColumnLabel}
                            />
                        </Box>

                        <Box>
                            <Typography sx={sectionTitleSx}>{t('BookCommands')}</Typography>
                            <TransactionRowsTable
                                rows={match.internalTransactions}
                                formatDate={formatDate}
                                t={t}
                                typeLabel="internal"
                                amountColumnLabel={amountColumnLabel}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 1.5 }}>
                {match && onRemoveMatch && (
                    <Button
                        variant="outlined"
                        onClick={() => onRemoveMatch(match)}
                        disabled={loading || isRemovingMatch}
                        sx={{
                            minWidth: 120,
                            borderColor: '#d32f2f',
                            color: '#d32f2f',
                            fontWeight: 600,
                            '&:hover': {
                                borderColor: '#c62828',
                                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                            },
                        }}
                    >
                        {t('RemoveMatch')}
                    </Button>
                )}
                <Button
                    onClick={onClose}
                    variant="contained"
                    disabled={isRemovingMatch}
                    sx={{
                        backgroundColor: '#304FFF',
                        '&:hover': { backgroundColor: '#1E3ACC' },
                        minWidth: 120,
                    }}
                >
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewTransactionMatchModal;
